import { IssueState, IssueType } from "@boark/shared/types/issue";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  IssueForm,
  issueFastumTicketsToFormFastumTickets,
  issueFormLinkToCreateLink,
  IssueFormValues,
  issueFormValuesToUpdate,
  issueLinksToFormLinks,
  issueRegistersToFormRecords,
} from "../../components/issue-form";
import { Modal } from "../../components/modal";
import { useGetAssociation } from "../../queries/get-association";
import { useGetAssociationContext } from "../../queries/get-association-context";
import { useGetIssue } from "../../queries/issue";
import { getId } from "../../utils/id";
import {
  addIssueLink,
  removeIssueLink,
  updateIssue,
} from "../../services/issue";
import { navigateBack } from "../../utils/navigate-back";
import { recordIdToId } from "../../utils/record-id-to-id";

export function IssueEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Issue ID is required");
  }

  const { data: issue, isLoading: isLoadingIssue } = useGetIssue(id);
  const queryClient = useQueryClient();
  const { data: associationContext } = useGetAssociationContext();
  const { data: association } = useGetAssociation(
    getId(associationContext?.id ?? ""),
  );
  const hasFastumIntegration = Boolean(association?.integrations?.fastum);

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<IssueFormValues>({
    title: "",
    description: "",
    tags: [],
    state: IssueState.TODO,
    type: IssueType.ISSUE,
    parentIdentifier: undefined,
    links: [],
    records: [],
    fastumTickets: [],
  });

  const isLoading = isLoadingIssue || loading;

  useEffect(() => {
    if (issue) {
      setFormValues({
        title: issue.title,
        description: issue.description ?? "",
        tags: issue.tags,
        state: issue.state,
        type: issue.type,
        parentIdentifier: issue.parent?.identifier,
        links: issueLinksToFormLinks(issue.links),
        records: issueRegistersToFormRecords(issue),
        fastumTickets: issueFastumTicketsToFormFastumTickets(
          issue.fastumTickets,
        ),
      });
    }
  }, [issue]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/issue/list");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <IssueForm
        typeDisabled
        hasError={hasError}
        issueIdentifier={issue?.identifier}
        loading={isLoading}
        values={formValues}
        onAddLinks={async (linksToAdd) => {
          if (!issue) {
            return;
          }
          const filtered = linksToAdd.filter((link) => {
            if (link.issueIdentifier === issue.identifier) {
              return false;
            }
            return !formValues.links.some(
              (existingLink) =>
                existingLink.issueIdentifier === link.issueIdentifier,
            );
          });
          if (filtered.length === 0) {
            return;
          }
          setLoading(true);
          try {
            let updatedIssue = issue;
            for (const link of filtered) {
              updatedIssue = await addIssueLink(
                id,
                issueFormLinkToCreateLink(link),
              );
            }
            setFormValues((prev) => ({
              ...prev,
              links: issueLinksToFormLinks(updatedIssue.links),
            }));
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["issue", id] }),
              queryClient.invalidateQueries({ queryKey: ["issues"] }),
            ]);
          } catch {
            setHasError(true);
          } finally {
            setLoading(false);
          }
        }}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onRemoveLink={async (link) => {
          if (!link.relationId) {
            return;
          }
          setLoading(true);
          try {
            const updatedIssue = await removeIssueLink(
              id,
              recordIdToId(link.relationId),
            );
            setFormValues((prev) => ({
              ...prev,
              links: issueLinksToFormLinks(updatedIssue.links),
            }));
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["issue", id] }),
              queryClient.invalidateQueries({ queryKey: ["issues"] }),
            ]);
          } catch {
            setHasError(true);
          } finally {
            setLoading(false);
          }
        }}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await updateIssue(
                id,
                issueFormValuesToUpdate(formValues, {
                  includeFastumTickets: hasFastumIntegration,
                }),
              );
              await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["issue", id] }),
                queryClient.invalidateQueries({ queryKey: ["issues"] }),
              ]);
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
